import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { User, Key, Mail, Shield, Calendar, Lock } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const Profile = () => {
  const { user: currentUser } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/profile');
      setUserProfile(response.data);
    } catch (error) {
      toast.error('Failed to fetch profile');
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (passwordData.currentPassword === passwordData.newPassword) {
      toast.error('New password must be different from current password');
      return;
    }
    
    try {
      await api.put(`/users/password/${currentUser.id}`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      toast.success('Password changed successfully');
      setShowPasswordModal(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      // Refresh profile to update last password change date
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to change password');
      console.error('Error changing password:', error);
    }
  };

  const getRoleBadgeColor = (role) => {
    return role === 'Admin' ? '#dc3545' : '#28a745';
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Loading profile...</p>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Profile not found</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div className="dashboard-header" style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <User size={28} />
          <h1>My Profile</h1>
        </div>
      </div>

      <div className="row">
        {/* Profile Information Card */}
        <div className="col-md-6">
          <div className="card" style={{ padding: '25px' }}>
            <h3 style={{ marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <User size={20} />
              Profile Information
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label style={{ 
                  fontSize: '12px', 
                  color: '#666', 
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '5px',
                  display: 'block'
                }}>
                  Username
                </label>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <User size={16} />
                  {userProfile.username}
                </div>
              </div>

              <div>
                <label style={{ 
                  fontSize: '12px', 
                  color: '#666', 
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '5px',
                  display: 'block'
                }}>
                  Email Address
                </label>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Mail size={16} />
                  {userProfile.email}
                </div>
              </div>

              <div>
                <label style={{ 
                  fontSize: '12px', 
                  color: '#666', 
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '5px',
                  display: 'block'
                }}>
                  Role
                </label>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: '500'
                }}>
                  <span 
                    className="badge"
                    style={{ 
                      backgroundColor: getRoleBadgeColor(userProfile.role),
                      color: 'white',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      fontSize: '14px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    {userProfile.role === 'Admin' && <Shield size={14} />}
                    {userProfile.role}
                  </span>
                </div>
                <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                  {userProfile.role === 'Admin' 
                    ? 'Full system access including user management'
                    : 'Full access except user management'}
                </small>
              </div>

              <div>
                <label style={{ 
                  fontSize: '12px', 
                  color: '#666', 
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '5px',
                  display: 'block'
                }}>
                  Account Created
                </label>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Calendar size={16} />
                  {new Date(userProfile.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              </div>

              {userProfile.createdBy && (
                <div>
                  <label style={{ 
                    fontSize: '12px', 
                    color: '#666', 
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '5px',
                    display: 'block'
                  }}>
                    Created By
                  </label>
                  <div style={{ 
                    fontSize: '16px', 
                    fontWeight: '500'
                  }}>
                    {userProfile.createdBy.username}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Security Card */}
        <div className="col-md-6">
          <div className="card" style={{ padding: '25px' }}>
            <h3 style={{ marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Lock size={20} />
              Security
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label style={{ 
                  fontSize: '12px', 
                  color: '#666', 
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '5px',
                  display: 'block'
                }}>
                  Password
                </label>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  padding: '12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px'
                }}>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '500' }}>
                      ••••••••
                    </div>
                    <small style={{ color: '#666' }}>
                      Last changed: {userProfile.lastPasswordChange 
                        ? new Date(userProfile.lastPasswordChange).toLocaleDateString()
                        : 'Never'}
                    </small>
                  </div>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => setShowPasswordModal(true)}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '5px' 
                    }}
                  >
                    <Key size={14} />
                    Change Password
                  </button>
                </div>
              </div>

              <div style={{ 
                padding: '15px',
                backgroundColor: '#fff3cd',
                borderRadius: '6px',
                border: '1px solid #ffeaa7'
              }}>
                <h5 style={{ 
                  fontSize: '14px', 
                  marginBottom: '10px',
                  color: '#856404'
                }}>
                  Security Recommendations
                </h5>
                <ul style={{ 
                  margin: 0, 
                  paddingLeft: '20px',
                  fontSize: '13px',
                  color: '#856404'
                }}>
                  <li>Use a strong password with at least 6 characters</li>
                  <li>Include a mix of letters, numbers, and symbols</li>
                  <li>Change your password regularly</li>
                  <li>Don't share your password with anyone</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div 
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '400px' }}
          >
            <h2>Change Password</h2>
            
            <form onSubmit={handlePasswordChange}>
              <div className="form-group">
                <label className="form-label">Current Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ 
                    ...passwordData, 
                    currentPassword: e.target.value 
                  })}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">New Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ 
                    ...passwordData, 
                    newPassword: e.target.value 
                  })}
                  placeholder="Minimum 6 characters"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Confirm New Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ 
                    ...passwordData, 
                    confirmPassword: e.target.value 
                  })}
                  placeholder="Re-enter new password"
                  required
                />
              </div>

              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordData({
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: ''
                    });
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                >
                  Change Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;